import type { Project } from '@/hooks/useProject';

export type ReadinessReason = 'ready' | 'noCollection' | 'noScript' | 'bibleMissing' | 'bibleFailed';

export interface Readiness {
  ready: boolean;
  reason: ReadinessReason;
}

/**
 * Can this project start a production?
 *
 * Mirrors the engine's own gate in `POST /api/projects/{id}/produce`, which checks:
 *   - footage    → a non-empty `iconik_collection_id`
 *   - generative → a non-empty `project.script`
 *
 * The subtlety is what `script` actually is. Two different fields are involved:
 *
 *   source_script  ← source_script.txt, the human prose you upload
 *   script         ← script.txt, the GENERATED shot-level production bible
 *
 * Uploading a script only writes `source_script` and flips `bible_status` to
 * "needs_generation". `script` is written by the translate step, and only when the
 * bible passes validation. So "I uploaded a script" is NOT the same as "I can
 * produce" — the crew executes the bible, not the prose.
 *
 * Gating on `script` rather than `bible_status` is deliberate: a producer can bring a
 * finished AV-JSON bible directly, in which case `script.txt` exists and translation
 * is skipped, leaving `bible_status` unset. `bible_status` is only consulted to
 * explain WHY a project isn't ready.
 *
 * Note the creative brief is NOT required — the engine reads it if present and
 * proceeds without it.
 */
export function productionReadiness(project: Project): Readiness {
  const isFootage = (project.production_type || 'generative') === 'footage';

  if (isFootage) {
    return project.iconik_collection_id?.trim() ? { ready: true, reason: 'ready' } : { ready: false, reason: 'noCollection' };
  }

  if (project.script?.trim()) return { ready: true, reason: 'ready' };
  if (!project.source_script?.trim()) return { ready: false, reason: 'noScript' };
  return { ready: false, reason: project.bible_status === 'failed' ? 'bibleFailed' : 'bibleMissing' };
}
